import { useEffect, useId, useState, type FormEvent, type ReactNode, type Ref } from "react";
import { Button } from "../../ui";

/**
 * The search box over the list, which runs `mail.search` across the scope's mailboxes
 * (architecture §6.5), with the inbox's own buttons beside it. The mailboxes are in the sidebar
 * and the quick-filter chips are gone (WP-056): `is:unread`, `has:attachment`, `tag:` and the
 * rest are search terms.
 */
export function SearchBar({
  query,
  placeholder,
  searchRef,
  onSearch,
  children,
}: {
  query: string | undefined;
  placeholder: string;
  searchRef: Ref<HTMLInputElement>;
  onSearch: (query: string | undefined) => void;
  /** Beside the search box (the inbox's Select and Compose buttons). */
  children?: ReactNode;
}) {
  const searchId = useId();
  const [text, setText] = useState(query === undefined ? "" : query);
  useEffect(() => setText(query === undefined ? "" : query), [query]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = text.trim();
    onSearch(q === "" ? undefined : q);
  };

  return (
    // As tall as the open conversation's header line (h-11), so the two sit level.
    <form role="search" onSubmit={submit} className="flex h-11 items-center gap-2 border-b border-border">
      <label htmlFor={searchId} className="sr-only">
        Search mail
      </label>
      <input
        id={searchId}
        ref={searchRef}
        type="search"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        aria-keyshortcuts="/"
        className="h-8 min-w-0 flex-1 rounded-md border-0 bg-surface-2 px-3 text-sm text-text placeholder:text-muted"
      />
      {query === undefined ? null : (
        <Button variant="ghost" onClick={() => onSearch(undefined)}>
          Clear
        </Button>
      )}
      {children}
    </form>
  );
}
